import type { Order } from '~api/tos';

import { Link as RouterLink } from 'react-router-dom';

import { Button } from '@ingrid/luterkort-react';

import { DetailsDrawer as Drawer } from '~src/components/DetailsDrawer/DetailsDrawer';
import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { useTranslation } from '~src/i18n/use-translation';
import { AppRoute } from '~src/main/routes';

import { getSourceLabel } from './source';

interface DetailsDrawerProps {
  order?: Order;
}

interface ActionProps {
  order: Order | undefined;
}

function OrderDrawerAction({ order }: ActionProps) {
  const { t } = useTranslation();

  return (
    <RoleGuard roles={['transport:edit']} withTooltip>
      <Button as={RouterLink} to={`${order?.id}/${AppRoute.SHIPMENTS_CREATE}`} key="create">
        {t('common.createShipment')}
      </Button>
    </RoleGuard>
  );
}

export function DetailsDrawer({ order }: DetailsDrawerProps) {
  const { t } = useTranslation();

  return (
    <Drawer
      actions={<OrderDrawerAction order={order} />}
      detailsHref={order?.id}
      nodes={[
        <Drawer.Plain label="common.orderId" value={order?.id} key="tos" />,
        <Drawer.Plain
          label="transportOrders.details.sessionid"
          value={order?.metadata?.session?.session_id}
          key="session"
        />,
        <Drawer.Plain
          label="transportOrders.details.masterSessionId"
          value={order?.metadata?.master_session_id}
          key="masterSession"
        />,
        <Drawer.Badge
          label="common.source"
          value={order?.metadata?.source ? t(getSourceLabel(order?.metadata?.source)) : undefined}
          key="source"
        />,
        <Drawer.Comma label="common.externalId" value={order?.external?.reference_ids} key="references" />,
        <Drawer.Carrier label="common.carrier" value={order?.metadata?.session?.method} key="carrier" />,
        <Drawer.Order label="common.orderDetails" value={order?.cart?.items} key="order" />,
        <Drawer.Currency
          label="transportOrders.list.totalValue"
          value={order?.cart?.total_value}
          currency={order?.cart?.currency}
          key="value"
        />,
        <Drawer.Address label="common.shipFrom" value={order?.sender?.contact_info?.address} key="from" />,
        <Drawer.Address label="common.shipTo" value={order?.receiver?.contact_info?.address} key="to" />,
        <Drawer.Date label="common.createdAt" value={order?.created_at} key="created" time />,
        <Drawer.Date label="common.updatedAt" value={order?.updated_at} key="updated" time />,
        <Drawer.Plain label="common.description" value={order?.notes?.description} key="description" />,
      ]}
    />
  );
}
